import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { useUserRole, type UserRole } from '../context/UserRoleContext';
import { useLang } from '../context/LangContext';
import { useTheme } from '../context/ThemeContext';
import { colors } from '../utils/theme';
import { t } from '../i18n';

type ThemeColors = ReturnType<typeof colors>;

const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'TryQuiPus';
const _dotIdx = APP_NAME.indexOf('.');
const APP_NAME_PRE = _dotIdx >= 0 ? APP_NAME.slice(0, _dotIdx) : APP_NAME;
const APP_NAME_SUF = _dotIdx >= 0 ? APP_NAME.slice(_dotIdx) : '';

interface CardFeature {
  label: string;
  active: boolean;
}

interface RoleCardProps {
  title: string;
  description: string;
  features: CardFeature[];
  selected: boolean;
  onClick: () => void;
  c: ThemeColors;
}

function RoleCard({ title, description, features, selected, onClick, c }: RoleCardProps) {
  return (
    <button
      onClick={onClick}
      className="relative text-left rounded-2xl border-2 p-6 transition-all duration-200 shadow-sm hover:shadow-md"
      style={{
        background: c.card,
        borderColor: selected ? 'var(--brand)' : c.border,
        boxShadow: selected ? '0 4px 14px var(--brand-soft)' : undefined,
      }}
    >
      {/* Check badge */}
      {selected && (
        <div className="absolute top-4 right-4">
          <CheckCircle2 size={20} className="text-cyan-500" />
        </div>
      )}

      <h3 className="text-lg font-bold mb-1" style={{ color: c.textPrimary }}>
        {title}
      </h3>
      <p className="text-sm mb-4 leading-relaxed" style={{ color: c.textSub }}>{description}</p>

      <ul className="space-y-2">
        {features.map((f) => (
          <li key={f.label} className="flex items-center gap-2 text-sm">
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: f.active ? c.positive : c.textMuted }} />
            <span style={{ color: f.active ? c.textNormal : c.textMuted }}>{f.label}</span>
          </li>
        ))}
      </ul>
    </button>
  );
}

export default function RoleSelector() {
  const { saveRole } = useUserRole();
  const [selected, setSelected] = useState<UserRole>(null);
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const c = colors(isDark);

  async function handleContinue() {
    if (!selected) return;
    await saveRole(selected);
    navigate('/dashboard');
  }

  const contadorFeatures: CardFeature[] = [
    { label: t('roleSelector.multiEmpresa', lang),     active: true },
    { label: t('roleSelector.librosPle', lang),        active: true },
    { label: t('roleSelector.auditoria', lang),        active: true },
    { label: t('roleSelector.asientosManuales', lang), active: true },
  ];

  const emprendedorFeatures: CardFeature[] = [
    { label: t('roleSelector.dashboardSimple', lang), active: true },
    { label: t('roleSelector.reportesSimple', lang),  active: true },
    { label: t('roleSelector.sinJerga', lang),        active: true },
    { label: t('roleSelector.sinLibrosPle', lang),    active: false },
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ background: c.canvas }}>
      {/* ES | EN toggle — top right */}
      <div className="fixed top-4 right-4 flex items-center gap-1 rounded-full shadow px-1 py-1"
        style={{ background: c.card, border: `1px solid ${c.border}` }}>
        <button
          onClick={() => setLang('es')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
          style={lang === 'es' ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { color: c.textSub }}
        >
          ES
        </button>
        <button
          onClick={() => setLang('en')}
          className="px-3 py-1 rounded-full text-xs font-semibold transition-all"
          style={lang === 'en' ? { background: 'var(--brand)', color: 'var(--on-brand)' } : { color: c.textSub }}
        >
          EN
        </button>
      </div>

      {/* Logo */}
      <div className="mb-8 text-center">
        <span className="brand-logo" style={{ fontSize: 24, color: c.textPrimary }}>
          {APP_NAME_PRE}
          {APP_NAME_SUF && <span className="brand-logo-suffix">{APP_NAME_SUF}</span>}<span className="brand-logo-tm">™</span>
        </span>
      </div>

      {/* Card container */}
      <div className="w-full max-w-2xl rounded-3xl shadow-xl p-8"
        style={{ background: c.card, border: `1px solid ${c.border}` }}>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2" style={{ color: c.textPrimary }}>
            {t('roleSelector.title', lang)}
          </h1>
          <p className="text-sm" style={{ color: c.textSub }}>
            {t('roleSelector.subtitle', lang)}
          </p>
        </div>

        {/* Role cards */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <RoleCard
            title={t('role.contador', lang)}
            description={t('roleSelector.contadorDesc', lang)}
            features={contadorFeatures}
            selected={selected === 'contador'}
            onClick={() => setSelected('contador')}
            c={c}
          />
          <RoleCard
            title={t('role.emprendedor', lang)}
            description={t('roleSelector.emprendedorDesc', lang)}
            features={emprendedorFeatures}
            selected={selected === 'emprendedor'}
            onClick={() => setSelected('emprendedor')}
            c={c}
          />
        </div>

        {/* Continue button */}
        <button
          onClick={handleContinue}
          disabled={!selected}
          className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-bold text-sm transition-all"
          style={
            selected
              ? { background: 'var(--brand)', color: 'var(--on-brand)', boxShadow: '0 4px 14px var(--brand-soft)' }
              : { background: c.subtleBg, color: c.textMuted, cursor: 'not-allowed' }
          }
        >
          <>{t('roleSelector.continue', lang)} <ArrowRight size={16} /></>
        </button>

        {/* Footer hint */}
        <p className="text-center text-xs mt-4" style={{ color: c.textMuted }}>
          {t('roleSelector.changeAfter', lang)}
        </p>
      </div>
    </div>
  );
}
